import * as React from "react";
import {
  Button,
  FormField,
  FormFieldProps,
  Header,
  Input,
  InputProps,
  RadioGroup,
  Select,
  SpaceBetween,
  Textarea,
  TextareaProps,
  Toggle,
  TokenGroup,
} from "@amzn/awsui-components-react";
import { FieldDefinition, ChoiceOption } from "@amzn/altar-sds-client";
import { UIField } from "../../../../common/UIField";
import { FieldConfiguration } from "@amzn/ask-legal-domain";
import { UIModel } from "../../../../../model/ui-model";
import {
  ChoiceOptionAdd,
  ChoiceOptionsEdit,
} from "../../../../advanced-list/fields/ChoiceFields/ChoiceOptionInput";
import { AdvancedListModel } from "../../../../../model/advanced-list-model";

export const ChoiceFieldAddEdit = (props: {
  value?: {
    fieldDef?: FieldDefinition;
    fieldVisibility?: boolean;
    fieldConfiguration?: FieldConfiguration.Choice;
  };
  onUpdated?: (value?: {
    fieldDef?: FieldDefinition;
    fieldVisibility?: boolean;
    fieldConfiguration?: FieldConfiguration.Choice;
  }) => void;
  onCancel?: () => void;
}) => {
  const [fieldColumnDisplayName, setFieldColumnDisplayName] =
    React.useState<FormFieldProps>({});
  const [columnDisplayName, setColumnDisplayName] = React.useState<InputProps>({
    value: props.value?.fieldDef?.displayName,
  });
  const [fieldColumnDescription, setFieldColumnDescription] =
    React.useState<FormFieldProps>({});
  const [columnDescription, setColumnDescription] = React.useState<TextareaProps>({
    value: props.value?.fieldDef?.description ?? "",
  });

  const [showOnLivePage, setShowOnLivePage] = React.useState<boolean>(true);
  const [allowFreeTextOption, setAllowFreeTextOption] = React.useState<boolean>(
    props.value?.fieldDef?.allowFreeTextOption ?? false
  );

  const [fieldChoiceType, setFieldChoiceType] = React.useState<FormFieldProps>(
    {}
  );
  const [fieldChoiceOptions, setFieldChoiceOptions] =
    React.useState<FormFieldProps>({});

  const [choiceFieldConfig, setChoiceFieldConfig] =
    React.useState<FieldConfiguration.Choice>({
      type: FieldConfiguration.ChoiceType.SINGLE,
    });
  const choiceOptionState = UIModel.State.use<ChoiceOption[]>({
    initialValue: [],
  });

  const [addChoiceOptionsEnabled, setAddChoiceOptionsEnabled] =
    React.useState<boolean>(false);
  const [editChoiceOptionsEnabled, setEditChoiceOptionsEnabled] =
    React.useState<boolean>(false);
  const [defaultChoiceKeys, setDefaultChoiceKeys] = React.useState<string[] | undefined>(
    props.value?.fieldDef?.defaultChoiceKeys
  );
  const prevKeysRef = React.useRef<string[] | undefined>();

  function validate() {
    let valid = true;
    if (columnDisplayName.value?.length > 0) {
      setFieldColumnDisplayName({
        errorText: "",
      });
      if (!columnDisplayName.value.match(new RegExp(AdvancedListModel.DISPLAY_NAME_REGEXP))) {
        setFieldColumnDisplayName({
          errorText: "Column Display name is invalid",
        });
        valid = false;
      }
    } else {
      setFieldColumnDisplayName({
        errorText: "Column Display Name is required",
      });
      valid = false;
    }

    if (columnDescription.value?.length > AdvancedListModel.COLUMN_INFO_CHAR_LIMIT) {
      setFieldColumnDescription({
        errorText: "Column Info exceeds maximum character limit",
      });
      valid = false;
    } else {
      setFieldColumnDescription({
        errorText: "",
      });
    }

    if (choiceFieldConfig?.type?.trim().length > 0) {
      setFieldChoiceType({
        errorText: "",
      });
    } else {
      setFieldChoiceType({
        errorText: "Choice type selection is required",
      });
      valid = false;
      return;
    }

    if (choiceOptionState.value.length <= 0) {
      setFieldChoiceOptions({
        errorText: "Add atleast one choice option",
      });
      valid = false;
      return;
    } else {
      setFieldChoiceOptions({
        errorText: "",
      });
    }

    if (valid) {
      const updatedPayload = {
        fieldDef: {
          ...props.value.fieldDef,
          displayName: columnDisplayName.value!,
          allowFreeTextOption: allowFreeTextOption,
          choiceOptions: choiceOptionState.value,
          description: columnDescription.value?.length ? columnDescription.value : undefined,
          defaultChoiceKeys: defaultChoiceKeys,
        },
        fieldConfiguration: {
          ...props.value.fieldConfiguration,
          type: choiceFieldConfig.type,
        },
        fieldVisibility: showOnLivePage,
      };
      props.onUpdated?.(updatedPayload);
    }
  }
  React.useEffect(() => {
    validate();
  }, [
    columnDisplayName.value,
    columnDescription.value,
    choiceFieldConfig.type,
    choiceOptionState.value,
    showOnLivePage,
    allowFreeTextOption,
    defaultChoiceKeys
  ]);

  React.useEffect(() => {
    if (!props.value?.fieldDef) return;
    const fieldDef = props.value.fieldDef;
    const fieldConfig = props.value.fieldConfiguration;
    const choiceOptions = fieldDef.choiceOptions ?? [];
    setColumnDisplayName({ value: fieldDef.displayName });
    setColumnDescription({ value: fieldDef.description });
    setAllowFreeTextOption(fieldDef.allowFreeTextOption ?? false);
    setChoiceFieldConfig({
      type: fieldConfig?.type ?? FieldConfiguration.ChoiceType.SINGLE,
    });
    choiceOptionState.setValue(choiceOptions);

    const validOptionKeys = new Set(choiceOptions.map((option) => option.key));
    const filteredDefaultKeys = (fieldDef.defaultChoiceKeys ?? []).filter((key) =>
      validOptionKeys.has(key)
    );
    const previousDefaultKeys = prevKeysRef.current ?? [];
    const isSameAsPrevious =
      filteredDefaultKeys.length === previousDefaultKeys.length &&
      filteredDefaultKeys.every((key, index) => key === previousDefaultKeys[index]);
    if (!isSameAsPrevious) {
      setDefaultChoiceKeys(filteredDefaultKeys);
      prevKeysRef.current = filteredDefaultKeys;
    }
    setShowOnLivePage(props.value.fieldVisibility);
  }, [props.value]);

  return (
    <SpaceBetween direction="vertical" size="m">
      <SpaceBetween direction="vertical" size="xxs">
        <Header headingTagOverride="h4">Choice Column Details</Header>
        <UIField.CustomField
          name={"Column Display Name"}
          description={"How should column to be displayed on live page"}
          errorText={fieldColumnDisplayName.errorText}
          child={
            <Input
              value={columnDisplayName.value}
              onChange={(e) => setColumnDisplayName({ value: e.detail.value })}
              placeholder={"Enter Column Name"}
            />
          }
        />
        <UIField.CustomField
          name={"Column Info"}
          description={"Describe how the user should fill this field"}
          errorText={fieldColumnDescription.errorText}
          child={
            <Textarea
              value={columnDescription.value}
              onChange={(e) => setColumnDescription({ value: e.detail.value })}
              placeholder={"Enter Column Info"}
            />
          }
        />
        <UIField.CustomField
          name="Choice Column Type"
          errorText={fieldChoiceType.errorText}
          child={
            <RadioGroup
              items={[
                { value: FieldConfiguration.ChoiceType.SINGLE, label: "Single" },
                { value: FieldConfiguration.ChoiceType.MULTIPLE, label: "Multiple" },
              ]}
              value={choiceFieldConfig.type}
              onChange={(e) =>
                setChoiceFieldConfig({
                  type: e.detail.value,
                })
              }
            />
          }
        />

        <SpaceBetween direction="horizontal" size="m">
          <Header
            headingTagOverride="h4"
            actions={
              <>
                <Button
                  variant="inline-icon"
                  iconName="edit"
                  disabled={props.value?.fieldDef?.choiceOptions?.length <= 0}
                  onClick={(e) => {
                    setEditChoiceOptionsEnabled(true);
                    setAddChoiceOptionsEnabled(false);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="inline-icon"
                  iconName="add-plus"
                  onClick={(e) => {
                    setEditChoiceOptionsEnabled(false);
                    setAddChoiceOptionsEnabled(true);
                  }}
                >
                  Add
                </Button>
              </>
            }
          >
            Choice Options:
          </Header>
          <FormField errorText={fieldChoiceOptions.errorText} />
        </SpaceBetween>

        {!addChoiceOptionsEnabled && !editChoiceOptionsEnabled && (
          <TokenGroup
            items={[
              ...choiceOptionState.value.map((choice) => ({
                label: choice.displayValue,
                value: choice.key,
              })),
            ]}
          />
        )}

        {addChoiceOptionsEnabled && (
          <ChoiceOptionAdd
            choiceOptions={choiceOptionState.value}
            onConfirm={(updatedChoiceOptions: ChoiceOption[]) => {
              choiceOptionState.setValue(updatedChoiceOptions);
              setAddChoiceOptionsEnabled(false);
            }}
            onCancel={() => {
              setAddChoiceOptionsEnabled(false);
            }}
          />
        )}

        {editChoiceOptionsEnabled && (
          <ChoiceOptionsEdit
            choiceOptions={choiceOptionState.value}
            onConfirm={(updatedChoiceOptions: ChoiceOption[]) => {
              choiceOptionState.setValue(updatedChoiceOptions);
              setEditChoiceOptionsEnabled(false);
            }}
            onCancel={() => {
              setEditChoiceOptionsEnabled(false);
            }}
          />
        )}
        {!addChoiceOptionsEnabled && !editChoiceOptionsEnabled && (
          <SpaceBetween direction="vertical" size="xs">
            <Header headingTagOverride="h5">Default Option</Header>
            <FormField>
              <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                <Select
                  selectedOption={
                    defaultChoiceKeys?.[0]
                      ? {
                          label:
                            choiceOptionState.value.find((o) => o.key === defaultChoiceKeys[0])?.displayValue || " ",
                          value: defaultChoiceKeys[0],
                        }
                      : null
                  }
                  onChange={(e) => {
                    const selectedKey = e.detail.selectedOption?.value;
                    setDefaultChoiceKeys(selectedKey ? [selectedKey] : []);
                  }}
                  options={choiceOptionState.value.map((option) => ({
                    label: option.displayValue,
                    value: option.key,
                  }))}
                  placeholder="Select default choice"
                />
                <Button
                  variant="primary"
                  onClick={() => setDefaultChoiceKeys([])}
                  disabled={!defaultChoiceKeys?.length}
                >
                  Clear
                </Button>
              </SpaceBetween>
            </FormField>
          </SpaceBetween>
        )}
        <SpaceBetween direction="vertical" size="xxs">
          <Header headingTagOverride="h4">Options</Header>
          <SpaceBetween size="s" direction="vertical">
            <Toggle
              checked={showOnLivePage}
              onChange={({ detail }) => {
                setShowOnLivePage(detail.checked);
              }}
            >
              Show column on live page
            </Toggle>
            <Toggle
              checked={allowFreeTextOption}
              onChange={({ detail }) => {
                setAllowFreeTextOption(detail.checked);
              }}
            >
              Allow free text options
            </Toggle>
          </SpaceBetween>
        </SpaceBetween>
      </SpaceBetween>
    </SpaceBetween>
  );
};
